import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/20/solid';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function CategoryCard({
  category,
  selected,
  setSelected,
  onClickEditCategory,
  onClickDuplicateCategory,
  needsReviewCount,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => {
        setSelected((prev) => ({
          ...prev,
          category: category,
          product: {},
          productid: '',
          itemid: '',
          optiongroupid: '',
          optionid: '',
        }));
      }}
      onDoubleClick={() => onClickEditCategory(category)}
      className={classNames(
        category.id === selected.category.id
          ? 'border-blue-400 bg-blue-50/70 text-slate-900 ring-1 ring-blue-200 dark:border-sky-400 dark:bg-slate-700 dark:text-slate-100 dark:ring-sky-500/30'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-300 dark:hover:bg-slate-600',
        isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
        'group mx-3 my-2 flex min-h-[5.25rem] items-center rounded-md border px-4 py-3 transition-all duration-150',
      )}
    >
      <div className="flex-1">
        <div
          key={category.name}
          className="flex text-sm font-medium"
          aria-current={category.id ? 'page' : undefined}
        >
          <div className="overflow-hidden">
            <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
              <span className="min-w-0 truncate text-[0.95rem] font-semibold">
                {category.name}
              </span>
              {needsReviewCount > 0 && (
                <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  {needsReviewCount}
                </span>
              )}
            </div>
            <p
              className={classNames(
                category.id === selected.category?.id
                  ? 'text-slate-500 dark:text-slate-300'
                  : 'text-slate-400',
                'mt-1 text-xs font-light whitespace-normal max-h-[2.5em]',
              )}
            >
              {category.description?.length > 40 ? (
                <span>{category.description.slice(0, 40)}...</span>
              ) : (
                <span>{category.description}</span>
              )}
            </p>
          </div>
        </div>
      </div>
      <div
        className="flex items-center"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Menu as="div" className="relative">
          <Menu.Button className="inline-flex items-center rounded-md p-1 text-slate-300 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200">
            <EllipsisVerticalIcon className="h-5 w-5" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-1 w-36 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-slate-700 dark:ring-slate-600">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? 'bg-slate-50 dark:bg-slate-600' : '',
                      'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                    )}
                    onClick={() => onClickEditCategory(category)}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Edit
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? 'bg-slate-50 dark:bg-slate-600' : '',
                      'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                    )}
                    onClick={() => onClickDuplicateCategory(category)}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    Duplicate
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}

export default CategoryCard;
